// The token the STT worker checks before it transcribes (LAN-1309). Rails
// issues it (/langua/stt_tokens, SttToken); the page sends it as the
// `stt_token` field of an upload and in the start frame of a real-time
// session. A failed fetch is never an error here: the upload still works while
// the worker only logs tokens, and a refused real-time session falls back to it.

// Refetch while there is still time to use the old token.
const REFRESH_MARGIN_MS = 120 * 1000;
// After a failed fetch (no secret configured, a network drop), wait before
// asking again rather than adding a request to every recording.
const RETRY_AFTER_MS = 60 * 1000;

const defaultCsrfToken = () => (typeof document === 'undefined' ? '' : document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '');

export class SttTokenProvider {
  constructor({ url = '/langua/stt_tokens', fetchImpl = null, csrfToken = defaultCsrfToken, now = () => Date.now() } = {}) {
    this.url = url;
    this.fetchImpl = fetchImpl || ((...args) => fetch(...args));
    this.csrfToken = csrfToken;
    this.now = now;
    this.token = null;
    this.expiresAt = 0;
    this.retryAt = 0;
    this.pending = null;
  }

  // The cached token while it has more than the margin left, else null.
  current() {
    return this.token && this.expiresAt - this.now() > REFRESH_MARGIN_MS ? this.token : null;
  }

  // Resolves to a usable token or null; never rejects. With maxWaitMs, gives up
  // waiting after that long (the fetch carries on and fills the cache), so a slow
  // Rails cannot hold up an upload the worker only logs the token for.
  get(maxWaitMs) {
    const cached = this.current();
    if (cached) return Promise.resolve(cached);
    if (this.now() < this.retryAt) return Promise.resolve(null);
    if (!this.pending) {
      this.pending = this.fetchToken().finally(() => { this.pending = null; });
    }
    if (maxWaitMs === undefined) return this.pending;
    return Promise.race([this.pending, new Promise((resolve) => setTimeout(() => resolve(null), maxWaitMs))]);
  }

  async fetchToken() {
    try {
      const response = await this.fetchImpl(this.url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'X-CSRF-Token': this.csrfToken() }
      });
      const data = response.ok ? await response.json() : null;
      if (!data || typeof data.token !== 'string' || !data.token) throw new Error('no token');
      this.token = data.token;
      this.expiresAt = Number(data.expires_at) * 1000;
      return this.current();
    } catch (error) {
      this.retryAt = this.now() + RETRY_AFTER_MS;
      return null;
    }
  }
}
